import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { useEffect, useState } from "react";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ButtonGroup,
  ConfirmButton,
  Row,
  CancelButton,
  InputContainer,
  FolderListContainer,
  FolderItem,
  FolderName,
} from "./AddToFolderModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";

import LottieIcon from "../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import { selectUser } from "../../store/slices/user/slice";
import { useSelector } from "react-redux";
import { selectToggleToFavoriteLoading } from "../../store/slices/model/slice";

const FAVORITE_FOLDER_ID = "favorite";

const AddToFolderModal = ({
  isOpen,
  onClose,
  isCreatingNew,
  setIsCreatingNew,
  onConfirm,
  closeCollectionModal,
  folders = [],
  openCreateFolderModal,
  isLoading = false,
  selectedCollectionFolders = [],
  video,
}) => {
  const [selectedFolders, setSelectedFolders] = useState([]);
  const user = useSelector(selectUser);
  const loading = useSelector(selectToggleToFavoriteLoading);

  useLockBodyScroll(isOpen);

  const isFavorite = user?.favoriteCollections?.includes(video?._id);

  useEffect(() => {
    if (selectedCollectionFolders) {
      const formattedFolders = folders.map((f) => f._id);
      const filteredFolders = selectedCollectionFolders.filter((f) =>
        formattedFolders.includes(f),
      );

      const updatedFolders = isFavorite
        ? [FAVORITE_FOLDER_ID, ...filteredFolders]
        : filteredFolders;

      setSelectedFolders(updatedFolders);
    }
  }, [folders, selectedCollectionFolders, isFavorite]);

  const handleFolderClick = (folderId) => {
    if (folderId === "create_new") {
      setIsCreatingNew(true);
      openCreateFolderModal();
      onClose();
      setSelectedFolders(selectedCollectionFolders);
    } else if (folderId === FAVORITE_FOLDER_ID) {
      setIsCreatingNew(false);
      setSelectedFolders((prev) =>
        prev.includes(FAVORITE_FOLDER_ID)
          ? prev.filter((id) => id !== FAVORITE_FOLDER_ID)
          : [FAVORITE_FOLDER_ID, ...prev],
      );
    } else {
      setIsCreatingNew(false);
      setSelectedFolders((prev) =>
        prev.includes(folderId)
          ? prev.filter((id) => id !== folderId)
          : [...prev, folderId],
      );
    }
  };

  const handleConfirm = () => {
    const wasFavoriteSelected = selectedFolders.includes(FAVORITE_FOLDER_ID);
    const regularFolders = selectedFolders.filter(
      (id) => id !== FAVORITE_FOLDER_ID,
    );

    const shouldToggleFavorite = wasFavoriteSelected !== isFavorite;

    onConfirm({
      folderIds: regularFolders,
      toggleFavorite: shouldToggleFavorite,
    });
  };

  const handleCancel = () => {
    if (!isLoading && !loading) {
      const updatedFolders = isFavorite
        ? [FAVORITE_FOLDER_ID, ...selectedCollectionFolders]
        : selectedCollectionFolders;
      setSelectedFolders(updatedFolders);
      setIsCreatingNew(false);
      onClose();
    }
  };

  const wasFavoriteSelected = selectedFolders.includes(FAVORITE_FOLDER_ID);
  const regularFolders = selectedFolders.filter(
    (id) => id !== FAVORITE_FOLDER_ID,
  );

  const hasFavoriteChange = wasFavoriteSelected !== isFavorite;
  const hasRegularFolderChanges =
    JSON.stringify(regularFolders.sort()) !==
    JSON.stringify([...selectedCollectionFolders].sort());

  const hasAnyChanges = hasFavoriteChange || hasRegularFolderChanges;
  const isConfirmDisabled = isLoading || loading || !hasAnyChanges;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>Add to Folder</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <InputContainer>
            <FolderListContainer $paddingRight={folders.length < 6}>
              <FolderItem
                onClick={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                $isSelected={selectedFolders.includes(FAVORITE_FOLDER_ID)}
              >
                <FolderName>Favorites</FolderName>
                <LottieIcon
                  animationData={checkboxAnimation}
                  trigger="toggle-reverse"
                  isActive={selectedFolders.includes(FAVORITE_FOLDER_ID)}
                  onActiveChange={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                  onClick={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                  strokeColor={"#4D9DFF"}
                  speed={2}
                  size={24}
                />
              </FolderItem>

              {folders &&
                folders.length > 0 &&
                folders.map((folder) => (
                  <FolderItem
                    key={folder._id}
                    onClick={() => handleFolderClick(folder._id)}
                    $isSelected={selectedFolders.includes(folder._id)}
                  >
                    <FolderName>{folder.name}</FolderName>

                    <LottieIcon
                      animationData={checkboxAnimation}
                      trigger="toggle-reverse"
                      isActive={selectedFolders.includes(folder._id)}
                      onActiveChange={() => handleFolderClick(folder._id)}
                      onClick={() => handleFolderClick(folder._id)}
                      strokeColor={"#4D9DFF"}
                      speed={2}
                      size={24}
                    />
                  </FolderItem>
                ))}
            </FolderListContainer>

            <FolderItem
              onClick={() => handleFolderClick("create_new")}
              $isSelected={isCreatingNew}
            >
              <FolderName>Create new folder</FolderName>
              <PlusCircleIcon />
            </FolderItem>
          </InputContainer>

          <ButtonGroup>
            <CancelButton
              onClick={handleCancel}
              disabled={isLoading || loading}
            >
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              $type="primary"
              disabled={isConfirmDisabled}
            >
              {isLoading || loading ? (
                <l-line-spinner size="20" stroke="2" speed="1" color="#000" />
              ) : (
                "Add"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default AddToFolderModal;
